import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from '../../config/routes';
import {
  reprocessFilterCountryCodeSelector,
  reprocessReprocessLoadingSelector,
  reprocessFindRequested,
  activeCountriesSelector,
} from '../../state';
import { LoadingButton } from '../buttons';
import { ReprocessFilters } from './ReprocessFilters';

const ActionContainer = styled.div`
  display: flex;
  width: max-content;
  margin-left: auto;
  justify-content: right;

  > button:first-of-type {
    margin-right: ${props => props.theme.spacing(2)};
  }

  ${props => props.theme.breakpoints.down('md')} {
    margin-left: auto;
    margin-right: auto;
    justify-content: center;
  }
`;

const ButtonApply = () => {
  const dispatch = useDispatch();
  const handleClickApply = useCallback(() => dispatch(reprocessFindRequested()), [dispatch]);
  const { t } = useTranslation();
  const loading = useSelector(reprocessReprocessLoadingSelector);
  const hasCountry = Boolean(useSelector(reprocessFilterCountryCodeSelector));
  const applyDisabled = !hasCountry;
  return (
    <LoadingButton
      color="primary"
      variant="contained"
      onClick={handleClickApply}
      loading={loading}
      disabled={applyDisabled}
    >
      {t('navigation.buttons.apply')}
    </LoadingButton>
  );
};

interface ButtonCreateProps {
  onCreate?: () => void;
}

const ButtonCreate = ({ onCreate }: ButtonCreateProps) => {
  const navigate = useNavigate();
  const handleClickApply = useCallback(
    () =>
      onCreate ? onCreate() : navigate(routes.protectedNestedRoutes.reprocess.children.create.path),
    [navigate, onCreate]
  );
  const { t } = useTranslation();
  const { loaded } = useSelector(activeCountriesSelector);
  const disabled = !loaded;

  return (
    <Button variant="contained" onClick={handleClickApply} disabled={disabled}>
      {t('reprocess.labels.create_reprocess')}
    </Button>
  );
};

interface ReprocessSearchProps {
  onCreate?: () => void;
}

const ReprocessSearch = ({ onCreate }: ReprocessSearchProps) => {
  const hasCountry = Boolean(useSelector(reprocessFilterCountryCodeSelector));
  const applyDisabled = !hasCountry;
  const countryRequiredIncomplete = applyDisabled;

  const actions = (
    <ActionContainer data-tour="process-actions">
      <ButtonCreate onCreate={onCreate} />
      <ButtonApply />
    </ActionContainer>
  );
  return (
    <ReprocessFilters
      actions={actions}
      applyDisabled={applyDisabled}
      countryRequiredIncomplete={countryRequiredIncomplete}
    />
  );
};

export default ReprocessSearch;
